type Variations = {
  [key: string]: boolean;
};

import { type TranslationKey, t } from '@avalara/bat-apps-shared/i18n';

const PASSWORD_STRENGTH_KEYS = {
  strength: 'client.password-strength.strength',
  weak: 'client.password-strength.weak',
  okay: 'client.password-strength.okay',
  good: 'client.password-strength.good',
  great: 'client.password-strength.great',
} as const satisfies Record<string, TranslationKey>;

type PasswordStrengthKey = (typeof PASSWORD_STRENGTH_KEYS)[keyof typeof PASSWORD_STRENGTH_KEYS];

const getEntropy = (password: string): number => {
  let entropy = 0;
  if (!password) {
    return entropy;
  }

  if (password.length < 8) {
    // too short
    entropy = 1;
  } else if (password.length < 10) {
    entropy = 2 + password.length * 2;
  } else {
    const lengthComp = ((password.length - 10) * 1.5).toFixed();
    entropy = 21 + Number.parseInt(lengthComp, 10);
  }

  const variations: Variations = {
    digits: /\d/.test(password),
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    nonWords: /\W/.test(password),
  };

  let variationCount = 0;
  for (const check of Object.keys(variations)) {
    variationCount += variations[check] === true ? 1 : 0;
  }
  if (variationCount === 4) {
    entropy += 18;
  } else if (variationCount === 3) {
    entropy += 12;
  } else if (variationCount === 2) {
    entropy += 6;
  }

  return entropy;
};

const getPasswordStrengthKey = (password: string): PasswordStrengthKey => {
  const entropy = Number.parseInt(getEntropy(password).toString(), 10);

  const okayMin = 24;
  const goodMin = 31;
  const greatMin = 40;

  if (entropy >= greatMin) {
    return PASSWORD_STRENGTH_KEYS.great;
  }
  if (entropy < greatMin && entropy >= goodMin) {
    return PASSWORD_STRENGTH_KEYS.good;
  }
  if (entropy < goodMin && entropy >= okayMin) {
    return PASSWORD_STRENGTH_KEYS.okay;
  }
  if (entropy > 0 && entropy < okayMin) {
    return PASSWORD_STRENGTH_KEYS.weak;
  }

  return PASSWORD_STRENGTH_KEYS.strength;
};

export const getPasswordStrength = (password: string): string => {
  return t(getPasswordStrengthKey(password));
};

type Props = {
  password: string;
  setStrengthText: (text: string) => void;
  setStrengthMeterClass: (attr: string) => void;
};

export const checkPasswordStrength = ({ password, setStrengthText, setStrengthMeterClass }: Props) => {
  const strengthKey = getPasswordStrengthKey(password);
  const strengthMeter = document.getElementById('strength-meter-gauge');
  const strengthText = document.getElementById('strength-text');
  setStrengthText(t(strengthKey));

  let className = '';
  if (strengthKey === PASSWORD_STRENGTH_KEYS.weak) {
    className = 'weak';
  } else if (strengthKey === PASSWORD_STRENGTH_KEYS.okay) {
    className = 'okay';
  } else if (strengthKey === PASSWORD_STRENGTH_KEYS.good) {
    className = 'good';
  } else if (strengthKey === PASSWORD_STRENGTH_KEYS.great) {
    className = 'great';
  } else if (strengthKey === PASSWORD_STRENGTH_KEYS.strength) {
    className = 'strength';
  }

  setStrengthMeterClass(className || '');
  strengthText?.setAttribute('class', `${className} strength-text`);
  strengthMeter?.setAttribute('class', `${className} form-inline strength-meter`);
};
